import {
  BookingState, CardioOrResistance, Gym, GymEnvironment,
  IHeartRate,
  IMetricResult, Level, WorkoutImportOrigin,
} from 'fitbeat.models';
import { ActivityType, UserWorkoutSession, UserProfile, WorkoutSettings } from '../../entities';
import { calculateMetrics } from '../../metrics/metric-calculator';
import { ActivityTypeMapping, CardioOrResistanceActivityTypeMapping } from '../../config';
import { Winston } from 'winston';
import { UserProfileService } from '../user';
import { WorkoutCompletionProcessing, WorkoutOverlapConflictHandling } from '../workout-completion-processing';

export interface IWorkoutImportCreateParams<SourceActivityType> {
  importOrigin: WorkoutImportOrigin;
  originId: string;
  externalActivityType: SourceActivityType;
  skillLevel: Level;
  userId: string;
  startTime: Date;
  endTime: Date;
  calories?: number;
  averageHeartRate?: number;
}

export interface IWorkoutImporterMapping<SourceUserIdentifier, SourceWorkoutData, SourceActivityType> {
  determineFitillionUser(source: SourceUserIdentifier): Promise<UserProfile>;
  isFitillionWorkout(workout: SourceWorkoutData): boolean;
  mapFromSourceWorkoutData(user: UserProfile, source: SourceWorkoutData,
  ): IWorkoutImportCreateParams<SourceActivityType>;
  getActivityTypeMapping(): Map<ActivityType, SourceActivityType[]>;
  getWorkoutImportOrigin(): WorkoutImportOrigin;
}

export class WorkoutImporter {
  private readonly cardioOrResistanceMapping = CardioOrResistanceActivityTypeMapping.buildActivityTypeMap();

  constructor(
    protected logger: Winston,
    protected environment: GymEnvironment,
    protected userProfileService: UserProfileService,
    protected workoutOverlapConflictHandling: WorkoutOverlapConflictHandling,
    protected completionProcessing: WorkoutCompletionProcessing,
  ) {}

  public async importWorkoutData<SourceUserIdentifier, SourceWorkoutData, SourceActivityType>(
    sourceWorkouts: SourceWorkoutData[],
    importMapping: IWorkoutImporterMapping<SourceUserIdentifier, SourceWorkoutData, SourceActivityType>,
    sourceUser: SourceUserIdentifier,
    requestDate: Date,
  ) {
    const fitillionUser = await importMapping.determineFitillionUser(sourceUser);
    const userId = fitillionUser.uid;
    const userHomeGym = await this.getUserHomeGym(fitillionUser);

    const activityTypeMapping = importMapping.getActivityTypeMapping();
    ActivityTypeMapping.validateThatAllActivityTypesAreMapped(activityTypeMapping);

    const fitillionWorkouts = sourceWorkouts.filter((w) => importMapping.isFitillionWorkout(w));
    const nonFitillionWorkouts = sourceWorkouts.filter((w) => !importMapping.isFitillionWorkout(w));

    const mappedWorkouts = [];
    for (let index = 0; index < nonFitillionWorkouts.length; index++) {
      const sourceWorkoutData = nonFitillionWorkouts[index];
      try {
        const workoutImportCreateParams = importMapping.mapFromSourceWorkoutData(
          fitillionUser,
          sourceWorkoutData,
        );

        const workout = this.createWorkoutSessionForImport<SourceActivityType>(
          workoutImportCreateParams,
          activityTypeMapping,
          fitillionUser,
            userHomeGym,
            index,
          );
        mappedWorkouts.push(workout);
      } catch (error) {
        this.logger.warn('[WorkoutImporter] Unexpected invalid import workout detected, not importing', {
          userId,
          sourceWorkoutData,
        });
      }
    }
    this.logger.info('[WorkoutImporter] Parsed import data', {
      userId,
      sourceWorkouts: sourceWorkouts.length,
      mappedWorkouts: mappedWorkouts.length,
      ignoredFitillionWorkouts: fitillionWorkouts,
    });

    await this.handleOverlapConflictsAndCompleteWorkouts(userId, mappedWorkouts, requestDate);
  }

  public async deregisterUserFromConnectedApp<SourceUserIdentifier, SourceWorkoutData, SourceActivityType>(
    importMapping: IWorkoutImporterMapping<SourceUserIdentifier, SourceWorkoutData, SourceActivityType>,
    sourceUser: SourceUserIdentifier,
  ) {
    const userProfile = await importMapping.determineFitillionUser(sourceUser);
    const updateUserProfile = UserProfile.removeWorkoutImportConfig(
      userProfile,
      importMapping.getWorkoutImportOrigin(),
    );
    await this.userProfileService.updateUserProfile(updateUserProfile.uid, updateUserProfile);
  }

  public async handleOverlapConflictsAndCompleteWorkouts(
    userId: string,
    workoutsRequestingImport: UserWorkoutSession[],
    requestDate: Date,
  ): Promise<void> {
    const validWorkouts = this.workoutOverlapConflictHandling.filterOutOverlappingWorkouts(workoutsRequestingImport);
    const failedImports = [];
    const completedImports = [];
    for (const workout of validWorkouts) {
      try {
        await this.completionProcessing.handleOverlapsAndCompleteImport(workout.userProfileId, workout, requestDate);
        completedImports.push(workout);
      } catch (e) {
        failedImports.push(workout);
        this.logger.error(`Failed to complete workout processing on import for ${workout.uniqueReference}`, e);
      }
    }

    this.logger.info(`completed workouts ${JSON.stringify({
      userId,
      completedImports: completedImports.length,
      failedImports: failedImports.length,
    })}`);
  }

  protected createWorkoutSessionForImport<SourceActivityType>(
    workoutImportCreateParams: IWorkoutImportCreateParams<SourceActivityType>,
    activityTypeMapping: Map<ActivityType, SourceActivityType[]>,
    fitillionUser: UserProfile,
    userHomeGym: Gym,
    index: number,
  ): UserWorkoutSession {
    const activityType = ActivityTypeMapping.getActivityTypeFor<SourceActivityType>(
      workoutImportCreateParams.externalActivityType,
      activityTypeMapping,
    ) || ActivityType.OtherCardio;
    const cardioOrResistance = CardioOrResistanceActivityTypeMapping
      .getCardioOrResistanceForActivityType(
        activityType,
        this.cardioOrResistanceMapping,
      );
    const level = workoutImportCreateParams.skillLevel;

    const performanceMetrics: IMetricResult | null =  this.buildWorkoutMetrics<SourceActivityType>(
      workoutImportCreateParams,
      cardioOrResistance,
      fitillionUser,
    );

    this.validateForImport(workoutImportCreateParams, activityType, cardioOrResistance, level, userHomeGym);
    const workoutSession = this.createForImport(
      workoutImportCreateParams,
      activityType,
      cardioOrResistance,
      level,
      performanceMetrics,
      userHomeGym,
      fitillionUser.gymName,
      index,
    );
    return workoutSession;
  }

  private createForImport<SourceType>(
    params: IWorkoutImportCreateParams<SourceType>,
    activityType: ActivityType,
    cardioOrResistance: CardioOrResistance,
    level: Level,
    performanceMetrics: IMetricResult | null,
    userHomeGym: Gym,
    memberMainGymName: string | undefined,
    index: number,
  ): UserWorkoutSession {
    const settings: WorkoutSettings = {
      activityType,
      cardioOrResistance,
      level,
      isDefaultDailyMix: false,
      allowBagContact: false,
      threeBodyGroups: WorkoutSettings.getFullBody(),
      equipment: [],
      ignoreProgramRecommendations: true,
    };
    const workoutSession = UserWorkoutSession.create(
      params.userId,
      {
        settings,
        creationDate: params.startTime,
        amountOfStages: 0, // N/A for imported workouts
        currentStage: undefined, // N/A for imported workouts
        earliestAllowedStartTime: undefined, // N/A for imported workouts
        provisional: undefined, // N/A for imported workouts
        gym: userHomeGym,
        uniqueReferenceSuffix: `_import${index}`,
      },
    );

    workoutSession.settings = settings;
    workoutSession.performanceMetrics = performanceMetrics;
    workoutSession.memberMainGym = memberMainGymName || userHomeGym.name;
    workoutSession.bookingState = BookingState.ConfirmedBooking;
    workoutSession.startTime = params.startTime;
    workoutSession.startTimeOfFirstExercise = params.startTime;
    workoutSession.checkedIn = true;
    workoutSession.gymCheckinTime = params.startTime;
    workoutSession.homeWorkoutStartedAt = params.startTime;
    workoutSession.earliestAllowedStartTime = params.startTime;
    workoutSession.endTime = params.endTime;
    workoutSession.workoutType = 1;
    workoutSession.importOrigin = {
      origin: params.importOrigin,
      externalReferenceId: params.originId,
      importDate: new Date(),
    };
    workoutSession.exerciseSessions = [];
    return workoutSession;
  }

  private validateForImport<SourceType>(
    params: IWorkoutImportCreateParams<SourceType>,
    activityType: ActivityType,
    cardioOrResistance: CardioOrResistance,
    level: Level,
    userHomeGym: Gym,
  ): void {
    if (!params) {
      throw new Error('Workout import parameters cannot be null or undefined');
    }
    if (!params.userId) {
      throw new Error('userId is required for workout import');
    }
    if (!params.startTime) {
      throw new Error('startTime is required for workout import');
    }
    if (!params.endTime) {
      throw new Error('endTime is required for workout import');
    }
    if (params.startTime > params.endTime) {
      throw new Error('startTime must be before endTime');
    }
    const workoutDuration = UserWorkoutSession.calculateWorkoutDurationInMinutes(params.startTime, params.endTime);
    if (workoutDuration > WorkoutOverlapConflictHandling.maxWorkoutTimeMinutes) {
      throw new Error('Imported workout duration cannot exceed 24 hours');
    }
    if (!params.importOrigin) {
      throw new Error('importOrigin is required for workout import');
    }
    if (!params.originId) {
      throw new Error('originId is required for workout import');
    }
    if (params.externalActivityType == null) {
      throw new Error('externalActivityType is required for workout import');
    }
    if (activityType == null) {
      throw new Error('ActivityType cannot be undefined or null');
    }
    if (cardioOrResistance == null) {
      throw new Error('CardioOrResistance cannot be undefined or null');
    }
    if (level == null) {
      throw new Error('Level cannot be undefined or null');
    }
    if (userHomeGym == null) {
      throw new Error('userHomeGym cannot be undefined or null');
    }
  }

  private async getUserHomeGym(user: UserProfile): Promise<Gym> {
    const userFeatures = await this.userProfileService.getEnabledFeaturesForUser(user.uid, this.environment);
    return userFeatures.workout.homeGym;
  }

  private buildWorkoutMetrics<SourceActivityType>(
    workoutImportCreateParams: IWorkoutImportCreateParams<SourceActivityType>,
    cardioOrResistance: CardioOrResistance,
    fitillionUser: UserProfile,
  ): IMetricResult | null {
    if (!workoutImportCreateParams.averageHeartRate) {
      return null;
    }

    const averageHeartRate: IHeartRate[] = [
      {
        heartRate: workoutImportCreateParams.averageHeartRate,
        timestamp: workoutImportCreateParams.startTime,
      },
      {
        heartRate: workoutImportCreateParams.averageHeartRate,
        timestamp: workoutImportCreateParams.endTime,
      },
    ];
    const metrics = calculateMetrics(
      fitillionUser,
      averageHeartRate,
      cardioOrResistance,
      workoutImportCreateParams.calories,
    );
    return metrics || null;
  }
}
